import { useAtom, useAtomValue } from 'jotai';
import { chatBackgroundWorkOf, chatTasksOf } from '@localagent/sdk/react';
import type { BackgroundWorkItem, ChatSession, SessionTask, SessionTasks } from '@localagent/sdk';
import { cn } from '@localagent/ui';
import { toast } from '@localagent/ui/components/sonner';
import { tasksPanelCollapsedAtom, tasksPanelLayoutAtom, tasksShowDoneAtom } from '../../../stores/layout/atoms';
import { TaskRow } from './TaskRow';
import { KeepWorkingControl, KeepWorkingStateLine } from './KeepWorkingControl';
import { RunningWorkSection } from './RunningWorkSection';
import { useVisibleRunningWork } from './running-work';

const NO_WORK: BackgroundWorkItem[] = [];

export type TasksPanelVariant = 'simple' | 'advanced';

export interface SessionTasksPanelProps {
  sessionId: string | null | undefined;
  chat: ChatSession | null;
  variant: TasksPanelVariant;
  testIdPrefix?: string;
}

function isFinished(t: SessionTask): boolean {
  return t.status === 'done' || t.status === 'abandoned';
}

function tasksSummary(tasks: SessionTasks | null, running: number): string {
  const list = tasks?.tasks ?? [];
  const runningPart = running > 0 ? ` · ${running} running` : '';
  if (list.length === 0) return `No tasks yet${runningPart}`;
  const done = list.filter((t) => t.status === 'done').length;
  const blocked = list.filter((t) => t.status === 'blocked').length;
  return `${done} of ${list.length} done${blocked > 0 ? ` · ${blocked} blocked` : ''}${runningPart}`;
}

export function SessionTasksPanel({ sessionId, chat, variant, testIdPrefix = '' }: SessionTasksPanelProps) {
  const tasks = useAtomValue(chatTasksOf(sessionId));
  const work = useAtomValue(chatBackgroundWorkOf(sessionId));
  const live = chat && !chat.isDraft && chat.sessionId === sessionId ? chat : null;
  const onKeepWorkingChange = live
    ? (on: boolean) => {
        live.setKeepWorking(on).catch((err: unknown) => {
          toast.error(`Could not turn Keep working ${on ? 'on' : 'off'}: ${err instanceof Error ? err.message : String(err)}`);
        });
      }
    : undefined;
  return (
    <SessionTasksPanelView
      tasks={tasks}
      work={work}
      variant={variant}
      testIdPrefix={testIdPrefix}
      onKeepWorkingChange={onKeepWorkingChange}
    />
  );
}

export interface SessionTasksPanelViewProps {
  tasks: SessionTasks | null;
  work?: readonly BackgroundWorkItem[];
  now?: number;
  variant: TasksPanelVariant;
  testIdPrefix?: string;
  onKeepWorkingChange?: (on: boolean) => void;
}

export function SessionTasksPanelView({ tasks, work = NO_WORK, now, variant, testIdPrefix = '', onKeepWorkingChange }: SessionTasksPanelViewProps) {
  const [simpleCollapsed, setSimpleCollapsed] = useAtom(tasksPanelCollapsedAtom);
  const [layout, setLayout] = useAtom(tasksPanelLayoutAtom);
  const [showDone, setShowDone] = useAtom(tasksShowDoneAtom);
  const list = tasks?.tasks ?? [];
  const visibleWork = useVisibleRunningWork(work, now);
  const empty = list.length === 0 && visibleWork.running.length === 0 && visibleWork.recent.length === 0;
  const advanced = variant === 'advanced';
  const collapsed = advanced ? layout === 'collapsed' : simpleCollapsed;
  const open = !collapsed && !empty;
  const setCollapsed = (next: boolean) => (advanced ? setLayout(next ? 'collapsed' : 'split') : setSimpleCollapsed(next));
  const visible = showDone ? list : list.filter((t) => !isFinished(t));
  const bySeq = new Map(list.map((t) => [t.seq, t]));

  return (
    <section
      aria-label="Session tasks"
      data-testid={`${testIdPrefix}tasks-panel`}
      data-collapsed={open ? 'false' : 'true'}
      data-variant={variant}
      data-task-count={list.length}
      className={cn(
        'flex flex-col overflow-hidden rounded-lg border border-border bg-card',
        open ? 'min-h-0 flex-1' : 'flex-none',
      )}
    >
      <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {!empty && (
            <button
              type="button"
              data-testid={`${testIdPrefix}tasks-collapse`}
              aria-expanded={open}
              aria-label={open ? 'Collapse tasks' : 'Expand tasks'}
              onClick={() => setCollapsed(open)}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d={open ? 'M6 9l6 6 6-6' : 'M18 15l-6-6-6 6'} />
              </svg>
            </button>
          )}
          <span className="text-body font-semibold">Tasks</span>
          <span data-testid={`${testIdPrefix}tasks-summary`} className="truncate text-micro text-muted-foreground">
            {tasksSummary(tasks, visibleWork.running.length)}
          </span>
        </div>
        <div className="flex flex-none items-center gap-1">
          <KeepWorkingControl
            on={tasks?.keep_working.on ?? false}
            onChange={tasks ? onKeepWorkingChange : undefined}
            testIdPrefix={testIdPrefix}
          />
          {advanced && open && (
            <button
              type="button"
              data-testid={`${testIdPrefix}tasks-maximize`}
              aria-pressed={layout === 'maximized'}
              aria-label={layout === 'maximized' ? 'Restore split' : 'Maximize tasks'}
              title={layout === 'maximized' ? 'Restore split' : 'Maximize tasks'}
              onClick={() => setLayout(layout === 'maximized' ? 'split' : 'maximized')}
              className="rounded-md p-1 text-muted-foreground transition-colors hover:bg-accent"
            >
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
                <path d={layout === 'maximized' ? 'M4 14h6v6M20 10h-6V4M14 10l7-7M3 21l7-7' : 'M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7'} />
              </svg>
            </button>
          )}
        </div>
      </div>
      {!open && (
        <div className="flex items-center px-3 py-1.5">
          <KeepWorkingStateLine tasks={tasks} testIdPrefix={testIdPrefix} />
        </div>
      )}
      {open && (
        <>
          <RunningWorkSection work={visibleWork} testIdPrefix={testIdPrefix} />
          <ul data-testid={`${testIdPrefix}tasks-list`} className="min-h-0 flex-1 overflow-y-auto">
            {visible.map((t) => (
              <TaskRow key={t.seq} task={t} bySeq={bySeq} testIdPrefix={testIdPrefix} />
            ))}
          </ul>
          <div className="flex items-center justify-between gap-2 border-t border-border px-3 py-1.5 text-micro text-muted-foreground">
            <KeepWorkingStateLine tasks={tasks} testIdPrefix={testIdPrefix} />
            <label className="flex items-center gap-1.5" htmlFor={`${testIdPrefix}tasks-show-done`}>
              Show done
              <input
                id={`${testIdPrefix}tasks-show-done`}
                data-testid={`${testIdPrefix}tasks-show-done`}
                type="checkbox"
                checked={showDone}
                onChange={(e) => setShowDone(e.target.checked)}
              />
            </label>
          </div>
        </>
      )}
    </section>
  );
}
